import { Box, Grid, Typography } from '@mui/material';
import {
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_providerSearchResults as SEOSegmentationProviderSearchResult,
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults as SEOSegmentationCaregiverSearchResult,
} from '@/__generated__/getSearchResults';
import { SEOSegmentationPageType, SEOSegmentationSearchingFor } from '@/__generated__/globalTypes';
import { ServiceIdForMember } from '@/constants';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import ProviderLatestReview from './ProviderLatestReview';

interface ProviderReviewResultsProps {
  caregiverSearchResults: SEOSegmentationCaregiverSearchResult[];
  city?: string;
  pageType: string;
  seoSegmentId: string;
  state?: string;
  providerSearchResults: SEOSegmentationProviderSearchResult[];
}

const ProviderReviewResults = ({
  caregiverSearchResults,
  city,
  pageType,
  state,
  providerSearchResults,
}: ProviderReviewResultsProps) => {
  const {
    serviceIdForMember,
    segmentNameSingularLowerCase,
    segmentNamePluralLowerCase,
    searchingFor,
  } = usePageAttributes();

  const isTrustAndSafetyFlag =
    pageType === SEOSegmentationPageType.LOCAL &&
    searchingFor === SEOSegmentationSearchingFor.PROVIDER &&
    serviceIdForMember === ServiceIdForMember.PET_CARE;

  const renderReviewResults = () => {
    let headline;
    if (caregiverSearchResults.length > 0) {
      headline =
        pageType === SEOSegmentationPageType.NATIONAL
          ? `Recent ${segmentNameSingularLowerCase} reviews`
          : `Recent ${segmentNameSingularLowerCase} reviews in ${city}, ${state}`;
    } else {
      headline =
        pageType === SEOSegmentationPageType.NATIONAL
          ? `Recent reviews for local ${segmentNamePluralLowerCase}`
          : `Recent ${segmentNameSingularLowerCase} reviews in ${city}, ${state}`;
    }

    if (serviceIdForMember === ServiceIdForMember.CHILD_CARE) {
      headline =
        pageType === SEOSegmentationPageType.NATIONAL
          ? `Most recent reviews for local ${segmentNamePluralLowerCase}`
          : `Most recent reviews for ${segmentNamePluralLowerCase} in ${city}, ${state}`;
    }

    if (
      serviceIdForMember === ServiceIdForMember.BABYSITTER ||
      serviceIdForMember === ServiceIdForMember.NANNY
    ) {
      headline =
        pageType === SEOSegmentationPageType.NATIONAL
          ? `Recent ${segmentNameSingularLowerCase} reviews from local parents`
          : `Recent ${segmentNameSingularLowerCase} reviews from local parents in ${city}, ${state}`;
    }

    if (isTrustAndSafetyFlag) {
      headline = `Recent ${segmentNameSingularLowerCase} reviews from local pet parents in ${city}, ${state}`;
    }

    let allReviews;
    allReviews = providerSearchResults
      .filter((profile) => profile.latestReview !== null)
      .map((profile) => profile);

    if (caregiverSearchResults.length > 0) {
      allReviews = caregiverSearchResults
        .filter((profile) => profile.latestReview !== null)
        .map((profile) => profile);
    }

    return (
      <Grid item xs={12} md={10} style={{ margin: 'auto' }}>
        {allReviews.length >= 3 && (
          <>
            <Box mt={{ xs: 4, sm: 6 }} mb={3}>
              <Typography align="center" variant="h2">
                {headline}
              </Typography>
            </Box>

            {allReviews.map((profile: any, index) => {
              const profileName =
                profile.seoSegmentationProvider?.provider.name ||
                profile.caregiver?.member.displayName ||
                '';

              return (
                index < 5 && (
                  <Box key={`${profileName}-${index + 1}`}>
                    <Box mb={4}>
                      {profile.latestReview && (
                        <Grid container>
                          <Grid item xs={12}>
                            <Box
                              p={{ xs: 3, sm: '3, 6' }}
                              component="div"
                              sx={{
                                alignItems: 'flex-start',
                                border: '1px solid #CCD1D6',
                                borderRadius: '8px',
                                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
                                boxSizing: 'border-box',
                                justifyContent: 'center',
                              }}>
                              <ProviderLatestReview
                                latestReview={profile.latestReview}
                                name={profileName}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Box>
                )
              );
            })}
          </>
        )}
      </Grid>
    );
  };

  return renderReviewResults();
};

export default ProviderReviewResults;
